import { Link, usePage } from "@inertiajs/react"
import { ChevronRight } from "lucide-react"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"
import type { NavItem } from "@/types"

export function NavMain({
  items = [],
  label,
  onSearchClick,
  className,
  children,
}: {
  items: NavItem[]
  label?: string
  onSearchClick?: () => void
  className?: string
  // Extra SidebarMenuItems rendered before the items — custom entries (the
  // Inbox popover) render identically to the plain ones.
  children?: React.ReactNode
}) {
  const page = usePage()
  return (
    <SidebarGroup className={cn("px-2 py-0", className)}>
      {label && <SidebarGroupLabel>{label}</SidebarGroupLabel>}
      <SidebarMenu>
        {children}
        {items.map((item) => {
          const isSearch = item.title === "Search" && item.href === "#"

          if (isSearch && onSearchClick) {
            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  onClick={(e) => {
                    e.preventDefault()
                    onSearchClick()
                  }}
                  tooltip={{ children: item.title }}
                >
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          }

          if (item.children?.length) {
            const isGroupActive = item.children.some((child) =>
              page.url.startsWith(child.href),
            )

            return (
              <Collapsible
                key={item.title}
                asChild
                defaultOpen={isGroupActive}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip={{ children: item.title }}>
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.children.map((child) => (
                        <SidebarMenuSubItem key={child.title}>
                          <SidebarMenuSubButton
                            asChild
                            isActive={page.url.startsWith(child.href)}
                            className="data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[active=true]:font-medium"
                          >
                            <Link href={child.href} prefetch>
                              <span>{child.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )
          }

          return (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton
                asChild
                isActive={
                  !item.neverActive &&
                  (item.exactMatch
                    ? page.url === item.href
                    : page.url === item.href ||
                      page.url.startsWith(item.href + "/"))
                }
                tooltip={{ children: item.title }}
              >
                <Link href={item.href} prefetch>
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
